import { Platform, Alert } from 'react-native';
import { getFileType, processDataFile, processMediaFile } from '@utils/fileProcessingHelpers';

export interface FileHandlersConfig {
  dispatch: any;
  setActiveWindowComponent: (component: 'dataHandler' | null) => void;
  setAvailableSheets: (sheets: string[]) => void;
  setShowSheetSelector: (show: boolean) => void;
  setPendingWorkbook: (workbook: any) => void;
  setPendingFileName: (fileName: string) => void;
  setPendingMediaFile: (file: any) => void;
  setMetadataDialogVisible: (visible: boolean) => void;
  setPendingImageAnalysis: (analysis: any) => void;
}

/**
 * Handle dropped file processing
 */
export const createHandleDroppedFile = (config: FileHandlersConfig) => {
  const {
    dispatch,
    setActiveWindowComponent,
    setAvailableSheets,
    setShowSheetSelector,
    setPendingWorkbook,
    setPendingFileName,
    setPendingMediaFile,
    setMetadataDialogVisible,
    setPendingImageAnalysis,
  } = config;

  return async (file: File) => {
    console.log('📥 [EasyModeScreen] handleDroppedFile called', {
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
    });

    const fileType = getFileType(file.name, file.type);
    console.log('🔍 [EasyModeScreen] Detected file type:', fileType);

    const handleDataFileProcessed = () => {
      setActiveWindowComponent('dataHandler');
    };

    switch (fileType) {
      case 'csv':
      case 'excel':
        console.log('📊 [EasyModeScreen] Processing data file:', file.name);
        try {
          const result = await processDataFile(file, dispatch, handleDataFileProcessed);
          console.log('📊 [EasyModeScreen] processDataFile result:', result);
          
          if (result && result.hasMultipleSheets) {
            console.log('📑 [EasyModeScreen] Multiple sheets detected, showing selector', {
              sheetCount: result.sheetNames?.length || 0,
              sheetNames: result.sheetNames,
            });
            setAvailableSheets(result.sheetNames || []);
            setPendingWorkbook(result.workbook);
            setPendingFileName(file.name);
            setShowSheetSelector(true);
          } else {
            console.log('✅ [EasyModeScreen] File processed successfully (single sheet or CSV)');
          }
        } catch (error) {
          console.error('❌ [EasyModeScreen] Error processing data file:', error);
        }
        break;

      case 'image':
        console.log('🖼️ [EasyModeScreen] Processing image file:', file.name, {
          type: file.type,
          size: file.size,
          sizeMB: (file.size / 1024 / 1024).toFixed(2),
        });
        try {
          await processMediaFile(
            file,
            dispatch,
            setPendingMediaFile,
            setMetadataDialogVisible,
            setPendingImageAnalysis
          );
          console.log('✅ [EasyModeScreen] Image file processed successfully, analysis stored');
        } catch (error) {
          console.error('❌ [EasyModeScreen] Error processing image file:', error);
          Alert.alert('Error', `Failed to process image: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
        break;

      case 'video':
        console.log('🎥 [EasyModeScreen] Processing video file:', file.name, {
          type: file.type,
          size: file.size,
          sizeMB: (file.size / 1024 / 1024).toFixed(2),
        });
        try {
          await processMediaFile(
            file,
            dispatch,
            setPendingMediaFile,
            setMetadataDialogVisible
          );
          console.log('✅ [EasyModeScreen] Video file processed successfully, metadata dialog should open');
        } catch (error) {
          console.error('❌ [EasyModeScreen] Error processing video file:', error);
          Alert.alert('Error', `Failed to process video: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
        break;

      default:
        console.warn('⚠️ [EasyModeScreen] Unsupported file type:', fileType);
        Alert.alert(
          'Unsupported File',
          `File type "${fileType}" is not supported. Please drop CSV, Excel, image, or video files.`
        );
    }
  };
};

/**
 * Handle drop event
 */
export const createHandleDrop = (
  draggedOver: boolean,
  setDraggedOver: (value: boolean) => void,
  handleDroppedFile: (file: File) => Promise<void>
) => {
  return async (e: any) => {
    if (Platform.OS !== 'web') {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    
    // Clear the draggedOver state immediately to remove the hover border
    setDraggedOver(false);

    const files = Array.from(e.dataTransfer?.files || []);
    
    if (files.length === 0) {
      console.warn('⚠️ [EasyModeScreen] No files in drop event');
      return;
    }

    console.log(`📦 [EasyModeScreen] Dropped ${files.length} file(s):`, files.map((f: File) => ({
      name: f.name,
      type: f.type,
      size: f.size,
    })));

    for (const file of files) {
      const fileObj = file as File;
      await handleDroppedFile(fileObj);
    }
  };
};

/**
 * Handle drag over event
 * Throttles updates to prevent excessive state changes and log spam
 */
export const createHandleDragOver = (setDraggedOver: (value: boolean) => void) => {
  let lastUpdateTime = 0;
  let wasDraggedOver = false;
  const UPDATE_THROTTLE_MS = 150; // Throttle state updates to max once per 150ms
  
  return (e: any) => {
    if (Platform.OS !== 'web') {
      return;
    }
    
    e.preventDefault();
    e.stopPropagation();
    
    const now = Date.now();
    
    // Throttle updates to prevent excessive state changes and log spam
    if (now - lastUpdateTime < UPDATE_THROTTLE_MS) {
      return;
    }
    
    // Only update if we haven't already set it to true
    if (!wasDraggedOver) {
      wasDraggedOver = true;
      lastUpdateTime = now;
      setDraggedOver(true);
    }
  };
};

/**
 * Handle drag leave event
 * Uses a closure to track draggedOver state to avoid unnecessary checks
 */
export const createHandleDragLeave = (
  draggedOver: boolean,
  setDraggedOver: (value: boolean) => void
) => {
  let isDraggedOver = false;
  
  return (e: any) => {
    if (Platform.OS !== 'web') {
      return;
    }
    
    // Update internal state from prop (in case it changed externally)
    if (draggedOver !== isDraggedOver) {
      isDraggedOver = draggedOver;
    }
    
    if (!isDraggedOver) {
      return;
    }
    
    const currentTarget = e.currentTarget || e.target;
    const relatedTarget = e.relatedTarget || e.nativeEvent?.relatedTarget;

    if (!relatedTarget || (currentTarget && !currentTarget.contains(relatedTarget))) {
      e.preventDefault();
      e.stopPropagation();
      isDraggedOver = false;
      setDraggedOver(false);
    }
  };
};


